import { Session } from "@supabase/supabase-js";
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { supabase } from "./supabase-client";
import { LogeMember, Role } from "../types/entities";
import { touchLastActive } from "../api/members-api";

interface SupabaseContextType {
  session: Session | null;
  /** The roster row linked to this session's auth user, or null if they haven't signed up yet. */
  member: LogeMember | null;
  /** Roles as the UI should treat them — empty while previewing as a member. */
  roles: Role[];
  hasRole: (role: Role) => boolean;
  /**
   * Admin preview: render the app as a member with no roles, to check what
   * everyone else actually sees.
   *
   * This masks the interface only. The account keeps every real permission
   * server-side — RLS authorises against auth.uid(), which doesn't change —
   * so this is a preview, never a sandbox or a security boundary.
   */
  isViewingAsMember: boolean;
  /** Whether this account has any role worth masking in the first place. */
  canViewAsMember: boolean;
  setViewingAsMember: (on: boolean) => void;
  /** True until the initial session + member lookup has settled. */
  isLoading: boolean;
  refreshMember: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(
  undefined,
);

const fetchMemberAndRoles = async (userId: string) => {
  const { data: member, error: memberError } = await supabase
    .from("loge_members")
    .select("*")
    .eq("auth_user_id", userId)
    .maybeSingle();
  if (memberError) throw memberError;
  if (!member) return { member: null, roles: [] as Role[] };

  const { data: roleRows, error: rolesError } = await supabase
    .from("loge_member_roles")
    .select("role")
    .eq("loge_member_id", member.id);
  if (rolesError) throw rolesError;

  return { member, roles: (roleRows ?? []).map((r) => r.role) };
};

const EMPTY_ROLES: Role[] = [];

const VIEW_AS_KEY = "dss.viewAsMember";

const readStoredViewAs = () => {
  try {
    return window.sessionStorage.getItem(VIEW_AS_KEY) === "1";
  } catch {
    // Private mode and similar can throw on access rather than return null.
    return false;
  }
};

const SupabaseProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<LogeMember | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // sessionStorage, not localStorage: the preview should not outlive the app
  // being closed, but should survive the reloads you do while testing.
  const [isViewingAsMember, setIsViewingAsMember] = useState(
    () => readStoredViewAs(),
  );

  const loadMember = useCallback(async (userId: string | undefined) => {
    if (!userId) {
      setMember(null);
      setRoles([]);
      return;
    }
    const result = await fetchMemberAndRoles(userId);
    setMember(result.member);
    setRoles(result.roles);
    // Fire-and-forget: don't hold up rendering on this, and one missed
    // stamp just means a slightly stale "last active" later.
    if (result.member) touchLastActive().catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    // Which auth user the current `member`/`roles` state describes. Signing in
    // as someone new has to re-gate the app; a routine token refresh for the
    // same user must not, or the whole UI would blank every time the token
    // rolls over.
    let loadedFor: string | undefined;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      loadedFor = data.session?.user.id;
      await loadMember(data.session?.user.id);
      if (active) setIsLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return;
      setSession(next);

      const nextUserId = next?.user.id;
      if (nextUserId === loadedFor) return;
      loadedFor = nextUserId;

      // Re-gate while the lookup is in flight. setSession above is synchronous
      // but the member lookup is a round-trip, so without this there is a
      // window where the app has a session and a still-null member — and
      // RequireAuth reads exactly that as "signed in but hasn't picked a name
      // yet" and redirects to /signup. A returning member who had already
      // claimed their row got sent back to the signup screen every login.
      setIsLoading(true);
      loadMember(nextUserId).finally(() => {
        if (active) setIsLoading(false);
      });
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadMember]);

  const refreshMember = useCallback(
    () => loadMember(session?.user.id),
    [loadMember, session],
  );
  const setViewingAsMember = useCallback((on: boolean) => {
    setIsViewingAsMember(on);
    try {
      if (on) window.sessionStorage.setItem(VIEW_AS_KEY, "1");
      else window.sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {
      // Non-persistent is fine; the mode still works for this page view.
    }
  }, []);

  const canViewAsMember = roles.length > 0;
  const visibleRoles = isViewingAsMember ? EMPTY_ROLES : roles;
  const hasRole = useCallback(
    (role: Role) => visibleRoles.includes(role),
    [visibleRoles],
  );
  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return (
    <SupabaseContext.Provider
      value={{
        session,
        member,
        roles: visibleRoles,
        hasRole,
        isLoading,
        refreshMember,
        signOut,
        isViewingAsMember,
        canViewAsMember,
        setViewingAsMember,
      }}
    >
      {children}
    </SupabaseContext.Provider>
  );
};

const useSupabaseAuth = () => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error("useSupabaseAuth must be used within a SupabaseProvider");
  }
  return context;
};

export { SupabaseProvider, useSupabaseAuth };
